/**
 * Alert Configuration Modal
 * Modal for creating and editing wallet tracker alerts
 */

import { useState, useEffect } from 'react';
import { useAlertStore, type Alert, type AlertCondition, type AlertChannel } from '../../store/alertStore';
import { useWalletTrackerStore } from '../../store/walletTrackerStore';
import { useToast } from '../Toast';
import { Icons } from '../Icons';

interface AlertConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingAlert?: Alert | null;
  preselectedWallet?: string;
}

const CONDITION_OPTIONS: { value: AlertCondition; label: string; description: string; icon: any }[] = [
  {
    value: 'any_transaction',
    label: 'Any Transaction',
    description: 'Alert on any transaction from this wallet',
    icon: Icons.Activity,
  },
  {
    value: 'swap',
    label: 'Token Swap',
    description: 'Alert when wallet swaps tokens',
    icon: Icons.ArrowRightLeft,
  },
  {
    value: 'transfer_in',
    label: 'Incoming Transfer',
    description: 'Alert on tokens received',
    icon: Icons.ArrowRight,
  },
  {
    value: 'transfer_out',
    label: 'Outgoing Transfer',
    description: 'Alert on tokens sent',
    icon: Icons.ArrowRight,
  },
  {
    value: 'value_threshold',
    label: 'Value Threshold',
    description: 'Alert when transaction exceeds specified amount',
    icon: Icons.DollarSign,
  },
];

const VALUE_THRESHOLD_PRESETS = [
  { label: '$1K', value: 1000 },
  { label: '$10K', value: 10000 },
  { label: '$50K', value: 50000 },
  { label: '$100K', value: 100000 },
  { label: '$500K', value: 500000 },
];

export function AlertConfigModal({
  isOpen,
  onClose,
  editingAlert,
  preselectedWallet,
}: AlertConfigModalProps) {
  const { addAlert, updateAlert } = useAlertStore();
  const { wallets } = useWalletTrackerStore();
  const toast = useToast();

  const [selectedWallet, setSelectedWallet] = useState(preselectedWallet || '');
  const [condition, setCondition] = useState<AlertCondition>('any_transaction');
  const [valueThreshold, setValueThreshold] = useState<number>(10000);
  const [customValue, setCustomValue] = useState('');
  const [channels, setChannels] = useState<AlertChannel[]>(['in_app']);
  const [enabled, setEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load editing alert data
  useEffect(() => {
    if (editingAlert) {
      setSelectedWallet(editingAlert.walletAddress);
      setCondition(editingAlert.condition);
      setValueThreshold(editingAlert.valueThreshold || 10000);
      setChannels(editingAlert.channels);
      setEnabled(editingAlert.enabled);
    } else if (preselectedWallet) {
      setSelectedWallet(preselectedWallet);
    }
  }, [editingAlert, preselectedWallet]);

  // Reset form on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedWallet(preselectedWallet || '');
      setCondition('any_transaction');
      setValueThreshold(10000);
      setCustomValue('');
      setChannels(['in_app']);
      setEnabled(true);
    }
  }, [isOpen, preselectedWallet]);

  const toggleChannel = (channel: AlertChannel) => {
    if (channels.includes(channel)) {
      setChannels(channels.filter((c) => c !== channel));
    } else {
      setChannels([...channels, channel]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedWallet) {
      toast('Please select a wallet', 'warning');
      return;
    }

    if (channels.length === 0) {
      toast('Please select at least one notification channel', 'warning');
      return;
    }

    setIsSaving(true);

    try {
      const wallet = wallets.find((w) => w.address === selectedWallet);

      const alertData = {
        walletAddress: selectedWallet,
        walletLabel: wallet?.label,
        condition,
        valueThreshold: condition === 'value_threshold' ? valueThreshold : undefined,
        enabled,
        channels,
      };

      if (editingAlert) {
        updateAlert(editingAlert.id, alertData);
      } else {
        addAlert(alertData);
      }

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const selectedWalletData = wallets.find((w) => w.address === selectedWallet);
  const selectedConditionData = CONDITION_OPTIONS.find((opt) => opt.value === condition);

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '600px',
          width: '90%',
          backgroundColor: 'var(--background-primary)',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icons.Bell size={20} />
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
              {editingAlert ? 'Edit Alert' : 'Create New Alert'}
            </h2>
          </div>
          <button className="btn-ghost" onClick={onClose} style={{ padding: '8px' }} aria-label="Close">
            <Icons.X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
          {/* Wallet Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}
            >
              Wallet to Monitor
            </label>
            <select
              aria-label="Wallet to Monitor"
              value={selectedWallet}
              onChange={(e) => setSelectedWallet(e.target.value)}
              disabled={!!preselectedWallet}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: preselectedWallet
                  ? 'var(--background-secondary)'
                  : 'var(--background-primary)',
              }}
            >
              <option value="">Select a wallet...</option>
              {wallets.map((wallet) => (
                <option key={wallet.address} value={wallet.address}>
                  {wallet.label} ({wallet.address.slice(0, 10)}...)
                </option>
              ))}
            </select>
          </div>

          {/* Condition Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '12px',
                color: 'var(--text-primary)',
              }}
            >
              Alert Condition
            </label>
            <div style={{ display: 'grid', gap: '8px' }}>
              {CONDITION_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = condition === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setCondition(option.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '12px',
                      padding: '12px',
                      textAlign: 'left',
                      border: `2px solid ${
                        isSelected ? 'var(--color-primary)' : 'var(--border-color)'
                      }`,
                      borderRadius: '8px',
                      backgroundColor: isSelected
                        ? 'rgba(var(--color-primary-rgb), 0.1)'
                        : 'var(--background-secondary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{ marginTop: '2px' }}>
                      <Icon size={18} color={isSelected ? 'var(--color-primary)' : undefined} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          marginBottom: '2px',
                          color: isSelected ? 'var(--color-primary)' : 'var(--text-primary)',
                        }}
                      >
                        {option.label}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {option.description}
                      </div>
                    </div>
                    {isSelected && (
                      <Icons.Check size={18} style={{ color: 'var(--color-primary)' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Value Threshold (conditional) */}
          {condition === 'value_threshold' && (
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: 'var(--text-primary)',
                }}
              >
                Minimum Transaction Value (USD)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {VALUE_THRESHOLD_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => {
                      setValueThreshold(preset.value);
                      setCustomValue('');
                    }}
                    className={valueThreshold === preset.value ? 'btn-primary' : 'btn-ghost'}
                    style={{ fontSize: '13px', padding: '6px 12px' }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  $
                </span>
                <input
                  type="number"
                  value={customValue || valueThreshold}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setCustomValue(e.target.value);
                    setValueThreshold(value);
                  }}
                  placeholder="Enter custom amount"
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 28px',
                    fontSize: '14px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--background-primary)',
                  }}
                />
              </div>
            </div>
          )}

          {/* Notification Channels */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '12px',
                color: 'var(--text-primary)',
              }}
            >
              Notification Channels
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: channels.includes('in_app')
                    ? 'rgba(var(--color-primary-rgb), 0.1)'
                    : 'var(--background-secondary)',
                }}
              >
                <input
                  type="checkbox"
                  checked={channels.includes('in_app')}
                  onChange={() => toggleChannel('in_app')}
                  style={{ cursor: 'pointer' }}
                />
                <Icons.Bell size={16} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>In-App Notifications</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Show notifications within the application
                  </div>
                </div>
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  backgroundColor: 'var(--background-secondary)',
                }}
                title="Email notifications coming soon"
              >
                <input type="checkbox" disabled style={{ cursor: 'not-allowed' }} />
                <span style={{ fontSize: '12px' }}>📧</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>
                    Email <span style={{ fontSize: '10px' }}>(Coming Soon)</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Receive alerts via email
                  </div>
                </div>
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  backgroundColor: 'var(--background-secondary)',
                }}
                title="Webhook notifications coming soon"
              >
                <input type="checkbox" disabled style={{ cursor: 'not-allowed' }} />
                <Icons.Zap size={16} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>
                    Webhook <span style={{ fontSize: '10px' }}>(Coming Soon)</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Send to custom webhook URL
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Alert Preview */}
          {selectedWalletData && selectedConditionData && (
            <div
              style={{
                padding: '16px',
                backgroundColor: 'var(--background-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: 'var(--text-tertiary)',
                }}
              >
                Alert Preview:
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                You'll be notified when <strong>{selectedWalletData.label}</strong>{' '}
                {selectedConditionData.label.toLowerCase()}
                {condition === 'value_threshold' && (
                  <> worth more than <strong>${valueThreshold.toLocaleString()}</strong></>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="modal-footer"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '16px 24px',
            borderTop: '1px solid var(--border-color)',
          }}
        >
          <button className="btn-secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="spinner-inline" aria-hidden="true" />
                {editingAlert ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              editingAlert ? 'Update Alert' : 'Create Alert'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
